import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiFetch } from '../lib/api';
import { useChatSessions } from './useChatSessions';

const useSearchAndChat = ({
  userId,
  showToast,
  savedAlumniRows,
  checkFreemiumLimit,
  ensureUserRecordExists,
  incrementUsage,
  saveAlumni,
  refreshSavedAlumni,
}) => {
  const [query, setQuery] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [loadMoreLoading, setLoadMoreLoading] = useState(false);
  const [results, setResults] = useState([]);
  const messagesEndRef = useRef(null);
  const [offset, setOffset] = useState(0);
  const [hasMoreCandidates, setHasMoreCandidates] = useState(false);
  const [viewMode, setViewMode] = useState('cards');
  const [lastSearchQuery, setLastSearchQuery] = useState('');
  const [selectedUniversities, setSelectedUniversities] = useState(['ucsb']);
  const [candidateEmails, setCandidateEmails] = useState({});
  const [candidateSaved, setCandidateSaved] = useState({});
  const [emailLoading, setEmailLoading] = useState({});

  const appendFeedbackMessages = useCallback((payload, type = 'system') => {
    const messages = [];

    if (Array.isArray(payload?.guidance)) {
      messages.push(...payload.guidance.filter(message => typeof message === 'string' && message.trim()));
    } else if (typeof payload?.guidance === 'string' && payload.guidance.trim()) {
      messages.push(payload.guidance);
    }

    if (typeof payload?.message === 'string' && payload.message.trim()) {
      const normalizedMessage = payload.message.trim();
      if (!messages.some(existing => existing.trim() === normalizedMessage)) {
        messages.push(normalizedMessage);
      }
    }

    if (!messages.length) {
      return;
    }

    setResults(prev => [
      ...prev,
      ...messages.map(content => ({
        type,
        content,
      })),
    ]);
  }, []);

  const formatChatMessagesToResults = useCallback(messages => {
    const formattedResults = [];
    const sorted = messages.sort((a, b) => a.message_order - b.message_order);
    for (const message of sorted) {
      if (message.message_type === 'user_query') {
        formattedResults.push({ type: 'user', content: message.query_text });
      } else if (message.message_type === 'system_message') {
        formattedResults.push({ type: 'system', content: message.system_message });
      } else if (message.message_type === 'search_results' && message.results_data) {
        let data = [];
        if (Array.isArray(message.results_data)) {
          data = message.results_data;
        } else if (message.results_data.matches && Array.isArray(message.results_data.matches)) {
          data = message.results_data.matches;
        } else {
          data = [message.results_data];
        }
        formattedResults.push({
          type: 'assistant',
          content: data.map((result, idx) => ({
            ...result,
            id: `result_${message.id}_${idx}`,
            type: 'career_candidate',
          })),
        });
      } else if (message.message_type === 'error') {
        formattedResults.push({ type: 'error', content: message.error_message });
      }
    }
    return formattedResults;
  }, []);

  const {
    chats,
    currentChatId,
    showChatSidebar,
    setShowChatSidebar,
    loadingChats,
    creatingNewChat,
    setCreatingNewChat,
    isCreatingChat,
    setIsCreatingChat,
    newChatName,
    setNewChatName,
    editingChatId,
    setEditingChatId,
    editingChatName,
    setEditingChatName,
    currentChatSavedEmails,
    loadChatHistory,
    loadChatMessages,
    createNewChat,
    switchToChat,
    updateChatName,
    deleteChat,
    setCurrentChatSavedEmails,
    setCurrentChatId,
  } = useChatSessions({
    userId,
    savedAlumniRows,
    results,
    formatChatMessagesToResults,
    setResults,
    setCandidateSaved,
    setCandidateEmails,
    showToast,
  });

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [results]);

  const handleRateLimitError = useCallback((data, action) => {
    const errorMessage = data.detail?.error || `You have reached your daily ${action} limit. Please try again tomorrow.`;
    setResults(prev => [...prev, { type: 'error', content: errorMessage }]);
  }, []);

  const handleSearch = useCallback(
    async searchQuery => {
      if (!checkFreemiumLimit('searches_made')) {
        return;
      }
      setSearchLoading(true);
      setLastSearchQuery(searchQuery);
      setResults([{ type: 'user', content: searchQuery }]);
      setQuery('');
      setOffset(0);
      setHasMoreCandidates(true);
      setCandidateEmails({});
      setCandidateSaved({});
      setEmailLoading({});
      setViewMode('cards');

      try {
        const response = await apiFetch(`/query?offset=0&limit=3&user_id=${userId || ''}&universities=${selectedUniversities.join(',')}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            query: searchQuery,
            chat_id: currentChatId,
          }),
        });

        if (response.status === 429) {
          const errorData = await response.json();
          handleRateLimitError(errorData, 'search');
          setSearchLoading(false);
          return;
        }

        if (!response.ok) {
          try {
            const errorData = await response.json();
            if (errorData.guidance || errorData.message) {
              appendFeedbackMessages(errorData);
            } else {
              setResults(prev => [
                ...prev,
                {
                  type: 'system',
                  content:
                    'Sorry, that doesn\'t appear to be a valid search query. Here are some examples:\n\n"Software Engineers at Google" - Find alumni in specific roles at companies\n"Economics majors in consulting" - Find people with certain majors and careers',
                },
              ]);
            }
          } catch (error) {
            setResults(prev => [
              ...prev,
              {
                type: 'system',
                content:
                  'Sorry, that doesn\'t appear to be a valid search query. Here are some examples:\n\n"Software Engineers at Google" - Find alumni in specific roles at companies\n"Economics majors in consulting" - Find people with certain majors and careers',
              },
            ]);
          }
          setSearchLoading(false);
          return;
        }

        const data = await response.json();
        if (data.error || data.classification === 'error') {
          appendFeedbackMessages(data);
          setSearchLoading(false);
          return;
        }

        if (data.chat_id) {
          setCurrentChatId(data.chat_id);
        }

        if (data.needs_guidance || data.classification === 'non_search') {
          appendFeedbackMessages(data);
          setHasMoreCandidates(false);
          setSearchLoading(false);
          return;
        }

        if (data.classification === 'name_search' && data.message) {
          setResults(prev => [...prev, { type: 'system', content: data.message }]);
        }

        if (data.fallback_used && (data.guidance || data.message)) {
          appendFeedbackMessages(data);
        }

        if (data.tier_info?.message) {
          setResults(prev => [...prev, { type: 'system', content: data.tier_info.message }]);
        }

        if (Array.isArray(data.matches) && data.matches.length > 0) {
          setResults(prev => [
            ...prev,
            {
              type: 'assistant',
              content: data.matches.map((match, idx) => ({
                ...match,
                id: `result_${Date.now()}_${idx}`,
                type: 'career_candidate',
              })),
            },
          ]);
          setOffset(3);
          setHasMoreCandidates(data.tier_info ? data.tier_info.has_more : data.matches.length === 3);
          if (userId) {
            await incrementUsage('searches_made');
          }
        } else {
          if (data.guidance || data.message) {
            appendFeedbackMessages(data);
          }
          setHasMoreCandidates(false);
        }
      } catch (error) {
        setResults(prev => [
          ...prev,
          {
            type: 'system',
            content:
              'Sorry, that doesn\'t appear to be a valid search query. Here are some examples:\n\n"Software Engineers at Google" - Find alumni in specific roles at companies\n"Economics majors in consulting" - Find people with certain majors and careers',
          },
        ]);
      } finally {
        setSearchLoading(false);
      }
    },
    [appendFeedbackMessages, checkFreemiumLimit, currentChatId, handleRateLimitError, incrementUsage, selectedUniversities, userId],
  );

  const handleSubmit = useCallback(
    async event => {
      event.preventDefault();
      if (!query.trim()) return;
      await handleSearch(query);
    },
    [handleSearch, query],
  );

  const handleLoadMoreCandidates = useCallback(async () => {
    setLoadMoreLoading(true);
    try {
      const userMessages = results.filter(r => r.type === 'user');
      const lastQuery = userMessages.length > 0 ? userMessages[userMessages.length - 1].content : '';
      const response = await apiFetch(`/query?offset=${offset}&limit=3&user_id=${userId || ''}&universities=${selectedUniversities.join(',')}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query: lastQuery,
          chat_id: currentChatId,
        }),
      });
      const data = await response.json();
      if (Array.isArray(data.matches) && data.matches.length > 0) {
        if (data.tier_info?.message) {
          setResults(prev => [...prev, { type: 'system', content: data.tier_info.message }]);
        }
        const lastAssistantIdx = results.map(r => r.type).lastIndexOf('assistant');
        const newMatches = data.matches.map((match, idx) => ({
          ...match,
          id: `result_${Date.now()}_${offset + idx}`,
          type: 'career_candidate',
        }));
        if (lastAssistantIdx !== -1) {
          setResults(prev => {
            const next = [...prev];
            next[lastAssistantIdx] = {
              ...next[lastAssistantIdx],
              content: [...next[lastAssistantIdx].content, ...newMatches],
            };
            return next;
          });
        } else {
          setResults(prev => [...prev, { type: 'assistant', content: newMatches }]);
        }
        setOffset(offset + data.matches.length);
        setHasMoreCandidates(true);
      } else {
        setHasMoreCandidates(false);
      }
    } catch (error) {
      showToast('Failed to load more candidates.', 'error');
    } finally {
      setLoadMoreLoading(false);
    }
  }, [currentChatId, offset, results, selectedUniversities, showToast, userId]);

  const handleGetEmail = useCallback(
    async candidate => {
      if (!checkFreemiumLimit('emails_fetched')) {
        return;
      }
      
      // CRITICAL FIX: Prevent saving without a chat_id
      if (!currentChatId) {
        showToast('Please select or create a chat to save alumni', 'error');
        return;
      }
      
      setEmailLoading(prev => ({ ...prev, [candidate.id]: true }));
      try {
        const response = await apiFetch(`/get_work_email?user_id=${userId || ''}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            candidates: [
              {
                id: candidate.id,
                name: candidate.name,
                company: candidate.company_raw || candidate.company,
                current_position: candidate.current_position,
                linkedin_url: candidate.linkedin_url,
              },
            ],
          }),
        });
        const data = await response.json();
        if (response.status === 429) {
          showToast(data.detail?.error || 'You have reached your daily email lookup limit. Please try again tomorrow.', 'error');
          return;
        }
        const email = data.emails?.[0]?.email || '';
        const errorMessage = data.emails?.[0]?.error || '';
        setCandidateEmails(prev => ({ ...prev, [candidate.id]: email }));

        // CRITICAL: Pass chat_id so alumni is linked to the current chat
        // currentChatId is guaranteed to exist here due to check above
        await saveAlumni({
          alumni_name: candidate.name,
          alumni_position: candidate.current_position,
          alumni_company: candidate.company_raw || candidate.company,
          alumni_email: email || '',
          alumni_linkedin: candidate.linkedin_url,
          location: candidate.location || '',
          chat_id: currentChatId, // Link to current chat
        });
        setCandidateSaved(prev => ({ ...prev, [candidate.id]: true }));

        if (email && email.trim()) {
          setCurrentChatSavedEmails(prev => new Set([...prev, email.trim().toLowerCase()]));
        }

        if (userId) {
          const userExists = await ensureUserRecordExists(userId);
          if (userExists) {
            await incrementUsage('emails_fetched');
          }
        }

        // Refresh saved alumni for the current chat
        await refreshSavedAlumni(currentChatId);

        if (email && email.trim()) {
          showToast(`Email found and alumni saved to Connect page! Email: ${email}`, 'success');
        } else {
          const fallback =
            errorMessage ||
            'No email found for this person. This could be because they no longer work at this company, or their email is not publicly available.';
          showToast(
            `${fallback} However, the alumni has been saved to your Connect page with all available information. You can manually add their email later.`,
            'info',
          );
        }
      } catch (error) {
        showToast('Failed to get email.', 'error');
      }
      setEmailLoading(prev => ({ ...prev, [candidate.id]: false }));
    },
    [
      checkFreemiumLimit,
      currentChatId,
      ensureUserRecordExists,
      incrementUsage,
      refreshSavedAlumni,
      saveAlumni,
      showToast,
      setCurrentChatSavedEmails,
      userId,
    ],
  );

  useEffect(() => {
    if (userId) {
      loadChatHistory();
    }
  }, [loadChatHistory, userId]);

  const resetSearchState = useCallback(() => {
    setResults([]);
    setOffset(0);
    setHasMoreCandidates(false);
    setCandidateEmails({});
    setCandidateSaved({});
    setEmailLoading({});
    setSearchLoading(false);
    setLoadMoreLoading(false);
  }, []);

  const handleQuickSearch = useCallback(
    suggestion => {
      setQuery(suggestion);
      handleSearch(suggestion);
    },
    [handleSearch],
  );

  const handleToggleUniversity = useCallback(
    universityId => {
      setSelectedUniversities(prev => {
        if (prev.includes(universityId)) {
          return prev.filter(id => id !== universityId);
        }
        return [...prev, universityId];
      });
    },
    [setSelectedUniversities],
  );

  const resultCount = useMemo(() => {
    return results.reduce((count, result) => {
      if (result.type === 'assistant' && Array.isArray(result.content)) {
        return count + result.content.filter(item => item.type === 'career_candidate').length;
      }
      return count;
    }, 0);
  }, [results]);

  const savedCount = useMemo(() => {
    // Only show saved count when there are actual results displayed
    // This prevents showing stale saved counts from localStorage on page load
    if (resultCount > 0) {
      // When viewing results, show saved count from current chat's saved emails
      if (currentChatSavedEmails.size > 0) {
        return currentChatSavedEmails.size;
      }
      // Fallback to candidateSaved count if available
      return Object.values(candidateSaved).filter(Boolean).length;
    }
    // No results displayed, so don't show saved count
    return 0;
  }, [candidateSaved, currentChatSavedEmails, resultCount]);

  const hasResultSummary = useMemo(() => {
    // Only show result summary when there are actual results displayed
    // This prevents the header from showing "0 alumni found • X saved" on page load
    return resultCount > 0;
  }, [resultCount]);

  return {
    query,
    setQuery,
    searchLoading,
    loadMoreLoading,
    results,
    messagesEndRef,
    hasMoreCandidates,
    viewMode,
    setViewMode,
    lastSearchQuery,
    selectedUniversities,
    setSelectedUniversities,
    chats,
    currentChatId,
    showChatSidebar,
    setShowChatSidebar,
    loadingChats,
    creatingNewChat,
    setCreatingNewChat,
    isCreatingChat,
    setIsCreatingChat,
    newChatName,
    setNewChatName,
    editingChatId,
    setEditingChatId,
    editingChatName,
    setEditingChatName,
    candidateEmails,
    candidateSaved,
    emailLoading,
    currentChatSavedEmails,
    handleSearch,
    handleSubmit,
    handleLoadMoreCandidates,
    handleGetEmail,
    loadChatHistory,
    loadChatMessages,
    createNewChat,
    switchToChat,
    updateChatName,
    deleteChat,
    resetSearchState,
    handleQuickSearch,
    handleToggleUniversity,
    resultCount,
    savedCount,
    hasResultSummary,
  };
};

export { useSearchAndChat };
